<!--
  Angular 15: *ngIf / *ngFor only, no @if blocks.
-->
<div class="dl-page">

  <div class="dl-page__header">
    <div class="dl-page__heading">
      <h1 class="dl-page__title">Partners</h1>
      <p class="dl-page__subtitle" *ngIf="!initialLoading">{{ totalElements }} partner(s)</p>
    </div>
    <!-- Labelled, not icon-only: matTooltip needs hover, which a phone has not
         got, so an icon-only Add here was unlabelled on touch. -->
    <div class="dl-page__actions">
      <button
        type="button"
        class="dl-btn dl-btn--ghost partners-refresh"
        (click)="refreshPartners()"
        [disabled]="refreshing"
        matTooltip="Refresh partners"
        aria-label="Refresh partners">
        <mat-icon *ngIf="!refreshing">refresh</mat-icon>
        <mat-icon *ngIf="refreshing" class="partners-spin">hourglass_empty</mat-icon>
      </button>
      <a routerLink="/partners/add" class="dl-btn dl-btn--primary">
        <mat-icon>add</mat-icon>
        <span class="partners-add-label">Add partner</span>
      </a>
    </div>
  </div>

  <!-- FILTERS -->
  <div class="dl-card">
    <div class="dl-card__body">
      <div class="dl-filters">
        <div class="dl-field">
          <label class="dl-field__label" for="searchTerm">Search</label>
          <div class="partners-search">
            <!-- Never disable this input. A focused element that becomes disabled is blurred by
                 the browser, which is what used to kick the caret out mid-search. -->
            <input
              type="text"
              id="searchTerm"
              [(ngModel)]="searchTerm"
              (input)="onSearchTermChange()"
              placeholder="Name, email or phone"
              class="dl-input" />
            <button
              *ngIf="searchTerm"
              type="button"
              class="dl-btn dl-btn--ghost dl-btn--icon dl-btn--sm partners-search-clear"
              (click)="clearSearch()"
              matTooltip="Clear search"
              aria-label="Clear search">
              <mat-icon>close</mat-icon>
            </button>
          </div>
        </div>

        <div class="dl-field">
          <label class="dl-field__label" for="typeFilter">Type</label>
          <select id="typeFilter" class="dl-select" [(ngModel)]="selectedType" (change)="onTypeFilterChange()">
            <option value="">All types</option>
            <option *ngFor="let type of partnerTypes" [value]="type">{{ type }}</option>
          </select>
        </div>

        <div class="dl-field">
          <label class="dl-field__label" for="tenantFilter">Tenant</label>
          <select id="tenantFilter" class="dl-select" [(ngModel)]="tenantFilter" (change)="onTenantFilterChange()">
            <option value="">All</option>
            <option value="true">Tenants only</option>
            <option value="false">Non-tenants</option>
          </select>
        </div>

        <div class="dl-field">
          <label class="dl-field__label" for="pageSize">Per page</label>
          <select id="pageSize" class="dl-select" (change)="onPageSizeChange($event)">
            <option [value]="10" [selected]="pageSize === 10">10</option>
            <option [value]="25" [selected]="pageSize === 25">25</option>
            <option [value]="50" [selected]="pageSize === 50">50</option>
            <option [value]="100" [selected]="pageSize === 100">100</option>
          </select>
        </div>
      </div>
    </div>
  </div>

  <div *ngIf="initialLoading" class="dl-loading">
    <div class="dl-spinner"></div>
    <p>Loading partners…</p>
  </div>

  <div *ngIf="error" class="dl-banner dl-banner--error">
    <mat-icon>error</mat-icon>
    <div class="dl-banner__body">{{ error }}</div>
    <div class="dl-banner__actions">
      <button type="button" class="dl-btn dl-btn--sm" (click)="refreshPartners()">
        <mat-icon>refresh</mat-icon>
        Try again
      </button>
    </div>
  </div>

  <!-- Stays mounted through a failed refetch so the previous rows remain readable under the
       error banner; only a first load that fails hides it entirely. -->
  <div
    *ngIf="!initialLoading && (!error || partners.length > 0)"
    class="dl-card"
    [class.dl-is-refreshing]="refreshing">

    <app-empty-state
      *ngIf="partners.length === 0"
      icon="business"
      title="No partners found">
      Nothing matches the current search and filters.
    </app-empty-state>

    <table *ngIf="partners.length > 0" class="dl-table dl-table--cards" dlCardTable>
      <thead>
        <tr>
          <th>ID</th>
          <th>Logo</th>
          <th>Name</th>
          <th>Type</th>
          <th>Tenant</th>
          <th>Email</th>
          <th>Phone</th>
          <th>Address</th>
          <th><span class="partners-sr-only">Actions</span></th>
        </tr>
      </thead>
      <tbody>
        <tr *ngFor="let partner of partners; trackBy: trackByPartnerId">
          <!-- Both hidden on a phone: the id is a surrogate nobody quotes, and a
               32px logo earns no row of its own in a card. -->
          <td class="dl-cell--hide-sm">{{ partner.id || '-' }}</td>
          <td class="dl-cell--hide-sm">
            <span class="partners-logo">
              <img
                *ngIf="partner.logoAttachment?.url"
                [src]="partner.logoAttachment?.url"
                [alt]="partner.contact?.name + ' logo'">
              <mat-icon *ngIf="!partner.logoAttachment?.url">business</mat-icon>
            </span>
          </td>
          <td class="dl-cell--title">
            <a [routerLink]="['/partners', partner.id]">{{ partner.contact?.name || '-' }}</a>
          </td>
          <td>
            <span class="partner-type" [class]="'type-' + (partner.type || 'dentist').toLowerCase()">
              {{ partner.type || 'DENTIST' }}
            </span>
          </td>
          <td>
            <!-- Text rather than an icon-only tick, so the column reads without colour. -->
            <span class="tenant-badge" [class.tenant-yes]="partner.isTenant" [class.tenant-no]="!partner.isTenant">
              {{ partner.isTenant ? 'Tenant' : '—' }}
            </span>
          </td>
          <td>{{ partner.contact?.email || '-' }}</td>
          <td>{{ partner.contact?.phoneNumber || '-' }}</td>
          <td>{{ getFormattedAddress(partner) }}</td>
          <td class="dl-cell--bare">
            <a
              [routerLink]="['/partners', partner.id]"
              class="dl-btn dl-btn--sm dl-btn--icon"
              matTooltip="View details"
              aria-label="View details">
              <mat-icon>visibility</mat-icon>
            </a>
          </td>
        </tr>
      </tbody>
    </table>

    <app-pagination
      [currentPage]="currentPage"
      [pageSize]="pageSize"
      [totalElements]="totalElements"
      [totalPages]="totalPages"
      [loading]="refreshing"
      itemLabel="partners"
      (pageChange)="onPageChange($event)">
    </app-pagination>
  </div>
</div>
